import { MantineStyleProps } from '@mantine/core';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';
import {
  BaseFinancialItem,
  PensionAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import {
  FinancialItemGenericTable,
  formatDate,
  getFinancialItemType,
} from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { formatCurrency } from 'src/cashflows/utils';

type AccountsTableProps = {
  financialItems: PensionAsset[];
  metadata: DynamicUiEntitiesMetadata;
  pl?: MantineStyleProps['pl'];
  pr?: MantineStyleProps['pr'];
  titleParts?: string[];
  onSubmit: () => void;
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};

export default function PensionsTable({
  financialItems,
  metadata,
  pl = 50,
  pr = 112,
  onRowClick,
}: AccountsTableProps) {
  const pensionProductToUserFriendlyName = (pension: PensionAsset) => {
    const pensionProduct = pension.pensionProduct;

    if (pensionProduct === 'DefinedBenefitPension') {
      return 'Defined benefit';
    }

    if (pensionProduct === 'DefinedContributionPension') {
      return 'Defined contribution';
    }

    if (pensionProduct === 'PensionAnnuity') {
      return 'Annuity';
    }

    if (pensionProduct === 'IncomeDrawdownPension') {
      return 'Income drawdown';
    }

    return pensionProduct satisfies never;
  };

  const fieldsRenderingRules = [
    {
      headerTitle: 'Description',
      render: (item: PensionAsset) =>
        item.description?.length && item.description?.length > 50
          ? item.description.slice(0, 50) + '...'
          : item.description,
    },
    {
      headerTitle: 'Pension product',
      render: (item: PensionAsset) => pensionProductToUserFriendlyName(item),
    },
    {
      headerTitle: 'Provider',
      render: (item: PensionAsset) => item.provider || '',
    },
    {
      headerTitle: 'Provider Reference Number',
      render: (item: PensionAsset) => item.providerReferenceNumber || '',
    },
    {
      headerTitle: 'Type',
      render: (item: PensionAsset) => getFinancialItemType(item, metadata),
    },
    {
      headerTitle: 'Last updated',
      render: (item: PensionAsset) => formatDate(item.valueDate),
    },
    {
      headerTitle: 'Current value',
      render: (item: PensionAsset) => formatCurrency(item.value),
    },
  ];

  return (
    <>
      <FinancialItemGenericTable
        financialItems={financialItems}
        fieldsRenderingRules={fieldsRenderingRules}
        pl={pl}
        pr={pr}
        onRowClick={onRowClick}
      />
    </>
  );
}
